import { useCallback, useState } from "react";
import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";
import { twMerge } from "tailwind-merge";

import { tStatic } from "../../../i18n";

/**
 * Plain dark inputs for the public forms (sign-in, reset, support, order).
 * Replaces react-md's outlined TextField, whose floating label notches a box
 * into the border — the client asked for the "frames" to go.
 */
export const inputCls =
  "bg-[#1B2228] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-grey focus:border-primary outline-none w-full transition-colors";

const FieldShell = ({
  label,
  error,
  hint,
  htmlFor,
  children,
}: {
  label?: string;
  error?: string;
  hint?: string;
  htmlFor?: string;
  children: ReactNode;
}) => (
  <div className="flex flex-col gap-1 w-full text-left">
    {label && (
      <label htmlFor={htmlFor} className="text-xs text-grey">
        {label}
      </label>
    )}
    {children}
    {error ? (
      <span role="alert" className="text-red text-xs">
        {error}
      </span>
    ) : (
      hint && <span className="text-grey/70 text-xs">{hint}</span>
    )}
  </div>
);

type Common = { label?: string; error?: string; hint?: string };

export const TextInput = ({
  label,
  error,
  hint,
  className,
  id,
  ...props
}: Common & InputHTMLAttributes<HTMLInputElement>) => (
  <FieldShell label={label} error={error} hint={hint} htmlFor={id}>
    <input
      id={id}
      className={twMerge(inputCls, error && "border-red", className)}
      aria-invalid={!!error}
      {...props}
    />
  </FieldShell>
);

export const TextArea = ({
  label,
  error,
  hint,
  className,
  id,
  ...props
}: Common & TextareaHTMLAttributes<HTMLTextAreaElement>) => (
  <FieldShell label={label} error={error} hint={hint} htmlFor={id}>
    <textarea
      id={id}
      className={twMerge(inputCls, "min-h-[96px] resize-y", error && "border-red", className)}
      aria-invalid={!!error}
      {...props}
    />
  </FieldShell>
);

export const Select = ({
  label,
  error,
  hint,
  className,
  id,
  children,
  ...props
}: Common & SelectHTMLAttributes<HTMLSelectElement>) => (
  <FieldShell label={label} error={error} hint={hint} htmlFor={id}>
    <select
      id={id}
      className={twMerge(inputCls, "appearance-none", error && "border-red", className)}
      aria-invalid={!!error}
      {...props}
    >
      {children}
    </select>
  </FieldShell>
);

/** Password input with a show/hide toggle. */
export const PasswordInput = ({
  label,
  error,
  hint,
  className,
  id,
  ...props
}: Common & InputHTMLAttributes<HTMLInputElement>) => {
  const [show, setShow] = useState(false);
  return (
    <FieldShell label={label} error={error} hint={hint} htmlFor={id}>
      <div className="relative">
        <input
          id={id}
          type={show ? "text" : "password"}
          className={twMerge(inputCls, "pr-16", error && "border-red", className)}
          aria-invalid={!!error}
          {...props}
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] uppercase tracking-wide text-grey hover:text-white px-1"
        >
          {show ? tStatic("form.hide") : tStatic("form.show")}
        </button>
      </div>
    </FieldShell>
  );
};

// ------------------------------------------------------------- validation

export interface FieldRules {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  /** Regex source (tested against the whole value). */
  pattern?: string;
  /** Extra check — return an error message or "" when valid. */
  validate?: (value: string) => string;
}

export const validateValue = (value: string, rules: FieldRules): string => {
  const v = value.trim();
  if (rules.required && !v) return tStatic("validation.required");
  if (!v) return "";
  if (rules.minLength && v.length < rules.minLength)
    return tStatic("validation.tooShort", { min: rules.minLength });
  if (rules.maxLength && v.length > rules.maxLength)
    return tStatic("validation.tooLong", { max: rules.maxLength });
  if (rules.pattern && !new RegExp(rules.pattern).test(v))
    return tStatic("validation.pattern");
  return rules.validate ? rules.validate(v) : "";
};

/**
 * Minimal controlled field: value, an error that only shows after the field
 * was touched (blur) or a submit attempt, and the bound input props.
 */
export const useField = (rules: FieldRules = {}, initial = "") => {
  const [value, setValue] = useState(initial);
  const [touched, setTouched] = useState(false);

  const error = validateValue(value, rules);
  const reset = useCallback(() => {
    setValue(initial);
    setTouched(false);
  }, [initial]);

  return {
    value,
    setValue,
    /** Error to display (only once touched). */
    error: touched ? error : "",
    /** True when the current value fails validation, shown or not. */
    invalid: !!error,
    touch: () => setTouched(true),
    reset,
    props: {
      value,
      onChange: (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
      ) => setValue(e.target.value),
      onBlur: () => setTouched(true),
    },
  };
};

export const EMAIL_PATTERN =
  "^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\\.[a-zA-Z0-9-]+)*$";
export const PHONE_PATTERN = "^[+]?[(]?[0-9]{3}[)]?[-\\s.]?[0-9]{3}[-\\s.]?[0-9]{2,6}$";
